import React from 'react';
import { useCampo } from './Field';
import { molduraCampo } from './Input';

/**
 * Campo de texto longo do design system (at-64, frente D/D2).
 *
 * Nasce com a observação do checklist de vistoria, que era um `<textarea>` com
 * classe própria (`.checkTextarea`) — a quarta variação de moldura de campo do
 * app. Reusa a `molduraCampo` do `Input`, que foi exportada justamente para um
 * controle novo poder entrar com a mesma aparência.
 *
 * 📌 **Consumidor plural já existe no app** e é da frente D: `ModalDistrato`
 * (motivo do distrato), `ModalEditarTemplate` (corpo do termo),
 * `ModalEditarBikeEletrica` (observações) e `ModalEditarPlanoCotacao`. Criar aqui
 * um `<textarea>` com classes inline e extrair depois seria exatamente a
 * duplicação que a at-24 proíbe.
 *
 * ⚠️ **NÃO carrega `data-ds-campo`, e isso é medido.** Aquele atributo é metade
 * de um par: a outra é `input[type="text"]:not([data-ds-campo])` em
 * `ui/tokens/legado-compartilhado.css`, que **não alcança `<textarea>`**. Não há
 * nenhum seletor de elemento `textarea` no CSS legado (grep vazio em 10/09/2026),
 * então não há cascata a vencer aqui. Se um dia aparecer um, este componente
 * precisa do marcador e o seletor precisa do `:not()`.
 *
 * ⚠️ **`resize-y`, nunca `resize` livre**: redimensionar na horizontal estoura o
 * cartão da tela no mobile. E `rows` é 4 por padrão — a observação da vistoria é
 * de uma a três linhas na prática.
 */

interface PropsTextarea extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Estado de erro. Normalmente vem do `Field` pelo contexto — passar aqui é só
   * para uso solto, fora de um `Field`.
   */
  erro?: boolean;
}

/**
 * ⚠️ **`forwardRef` desde a at-69**, e não é conveniência: a ficha do template
 * insere placeholders **na posição do cursor** (`selectionStart`/`setSelectionRange`),
 * o que exige o nó real. Sem o `ref` a alternativa seria concatenar no fim do
 * texto — que é o comportamento de fallback do editor, não o desejado.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, PropsTextarea>(
  ({ erro, rows = 4, className = '', ...resto }, ref) => {
    const campo = useCampo();
    const temErro = erro ?? campo?.temErro ?? false;

    return (
      <textarea
        ref={ref}
        id={campo?.id}
        rows={rows}
        aria-invalid={temErro || undefined}
        aria-describedby={campo?.descricao}
        className={[molduraCampo(temErro), 'tw-resize-y', className].filter(Boolean).join(' ')}
        {...resto}
      />
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;
